import React, { useState } from 'react';
import {
  Search,
  Bell,
  Sun,
  Moon,
  Plus,
  ChevronDown,
  LogOut,
  Sparkles,
  HelpCircle,
  User as UserIcon,
  Key,
  Shield,
  Activity,
  Check,
  UserPlus,
  Menu,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext.tsx';
import { useTheme } from '../../context/ThemeContext.tsx';
import { useNotifications } from '../../context/NotificationContext.tsx';
import { useOnboarding } from '../../context/OnboardingContext.tsx';
import { Avatar } from '../common/Avatar.tsx';
import { RoleBadge } from '../common/Badge.tsx';
import { NotificationDropdown } from '../notifications/NotificationDropdown.tsx';
import { EditProfileModal } from '../profile/EditProfileModal.tsx';
import { ChangePasswordModal } from '../profile/ChangePasswordModal.tsx';
import { InviteModal } from '../team/InviteModal.tsx';
import { useEscapeKey } from '../../hooks/useEscapeKey.ts';

interface HeaderProps {
  currentViewTitle: string;
  onOpenSearch: () => void;
  onOpenCreateTask: () => void;
  onOpenCreateProject: () => void;
  onNavigate: (view: string) => void;
  onOpenMobileMenu?: () => void;
}

export const Header: React.FC<HeaderProps> = ({
  currentViewTitle,
  onOpenSearch,
  onOpenCreateTask,
  onOpenCreateProject,
  onNavigate,
  onOpenMobileMenu,
}) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { unreadCount, browserPermission, requestNotificationPermission } = useNotifications();
  const notificationsSupported = typeof window !== 'undefined' && 'Notification' in window;
  const { startTour } = useOnboarding();

  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isCreateMenuOpen, setIsCreateMenuOpen] = useState(false);
  const [isEditProfileOpen, setIsEditProfileOpen] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  useEscapeKey(isUserMenuOpen || isCreateMenuOpen, () => {
    setIsUserMenuOpen(false);
    setIsCreateMenuOpen(false);
  });

  return (
    <header className="h-13 sm:h-14 shrink-0 bg-transparent sticky top-0 z-30 px-3 sm:px-5 flex items-center justify-between gap-2 sm:gap-4 pt-safe">
      {/* Left: Mobile Drawer Trigger + View Title & Quick Action */}
      <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
        {onOpenMobileMenu && (
          <button
            onClick={onOpenMobileMenu}
            aria-label="Open mobile navigation menu"
            className="md:hidden p-2 rounded-full text-[#121417] dark:text-[#f3f4f6] hover:bg-white/70 dark:hover:bg-surface-800 transition-colors shrink-0"
          >
            <Menu className="w-5 h-5" />
          </button>
        )}

        <h1 className="text-sm sm:text-xl font-extrabold text-[#121417] dark:text-[#f3f4f6] tracking-tight uppercase font-sans truncate max-w-[110px] xs:max-w-[160px] sm:max-w-none">
          {currentViewTitle === 'Company Dashboard' ? 'WORKSPACE' : currentViewTitle}
        </h1>

        {user?.role !== 'viewer' && user?.role !== 'guest' && (
          <button
            data-tour="new-task-btn"
            onClick={() => {
              onOpenCreateTask();
            }}
            className="h-8 sm:h-9 px-2.5 sm:px-4 rounded-full bg-[#121417] hover:bg-black text-white text-xs font-bold flex items-center space-x-1 sm:space-x-1.5 shadow-neo transition-transform hover:scale-105 shrink-0"
          >
            <Plus className="w-3.5 h-3.5 text-brand-400 stroke-[3]" />
            <span className="hidden xs:inline">New Task</span>
            <span className="xs:hidden">Task</span>
          </button>
        )}

        <button
          onClick={() => setIsInviteOpen(true)}
          className="hidden sm:flex h-9 px-3.5 rounded-full bg-white dark:bg-[#181b20] border border-[#d5d8de] dark:border-surface-800 text-[#121417] dark:text-surface-100 text-xs font-bold items-center space-x-1.5 shadow-neo-card hover:scale-105 transition-transform shrink-0"
          title="Invite People to Workspace"
        >
          <UserPlus className="w-3.5 h-3.5 text-brand-600 dark:text-brand-400 stroke-[2.5]" />
          <span className="hidden sm:inline">Invite</span>
        </button>
      </div>


      {/* Right: Controls, Notifications & Profile */}
      <div className="flex items-center space-x-1.5 sm:space-x-3 shrink-0">
        {/* Guided Tutorial Tour Launcher */}
        <button
          onClick={startTour}
          className="hidden md:flex h-10 px-3.5 rounded-full bg-white dark:bg-[#181b20] border border-[#d5d8de] dark:border-surface-800 text-[#121417] dark:text-brand-300 font-bold text-xs items-center space-x-1.5 shadow-neo-card hover:scale-105 transition-transform"
          title="Take Guided Product Tour"
        >
          <Sparkles className="w-3.5 h-3.5 text-brand-500 stroke-[2.5]" />
          <span>Tour</span>
        </button>

        {/* Prominent Search Bar Pill on Desktop & Responsive Button on Mobile */}
        <button
          onClick={onOpenSearch}
          className="h-10 px-3 sm:px-3.5 rounded-full bg-white dark:bg-[#181b20] border border-[#d5d8de] dark:border-surface-800 text-[#475467] dark:text-[#f3f4f6] flex items-center space-x-2 hover:scale-[1.02] hover:border-brand-500/50 transition-all shadow-neo-card group"
          title="Search workspace (Ctrl+K or ⌘K)"
          aria-label="Search projects, tasks, members, and files"
        >
          <Search className="w-4 h-4 text-surface-400 group-hover:text-brand-500 transition-colors" />
          <span className="hidden lg:inline text-xs font-medium text-surface-400 dark:text-surface-500 pr-1">
            Search workspace...
          </span>
          <kbd className="hidden sm:inline-flex items-center px-1.5 py-0.5 text-[10px] font-mono font-semibold text-surface-400 dark:text-surface-500 bg-surface-100 dark:bg-surface-800 rounded border border-surface-200 dark:border-surface-700">
            ⌘K
          </kbd>
        </button>

        {/* Theme Toggle Button */}
        <button
          onClick={toggleTheme}
          aria-label="Toggle Theme"
          className="w-10 h-10 rounded-full bg-white dark:bg-[#181b20] border border-[#d5d8de] dark:border-surface-800 text-[#475467] dark:text-[#f3f4f6] flex items-center justify-center hover:scale-105 transition-transform shadow-neo-card"
        >
          {theme === 'dark' ? <Sun className="w-4 h-4 text-amber-400" /> : <Moon className="w-4 h-4 text-[#121417]" />}
        </button>

        {/* Notification Bell */}
        <div className="relative">
          <button
            onClick={() => setIsNotifOpen(!isNotifOpen)}
            aria-label="Notifications"
            className="w-10 h-10 rounded-full bg-white dark:bg-[#181b20] border border-[#d5d8de] dark:border-surface-800 text-[#475467] dark:text-[#f3f4f6] flex items-center justify-center hover:scale-105 transition-transform shadow-neo-card relative"
          >
            <Bell className="w-4 h-4" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-extrabold flex items-center justify-center border-2 border-white dark:border-[#181b20]">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>

          <NotificationDropdown
            isOpen={isNotifOpen}
            onClose={() => setIsNotifOpen(false)}
            onNavigate={(url) => {
              if (url.startsWith('/projects/')) {
                const parts = url.split('?');
                const projId = parts[0].replace('/projects/', '');
                onNavigate(`project-${projId}`);
              } else if (url.startsWith('/chat')) {
                onNavigate('chat');
              }
            }}
          />
        </div>

        {/* User Avatar Circle */}
        <div className="relative">
          <button
            onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
            className="flex items-center p-0.5 rounded-full border-2 border-white dark:border-surface-800 shadow-neo hover:scale-105 transition-transform"
            title="Account & Profile"
          >
            <Avatar src={user?.avatarUrl} name={user?.fullName || 'User'} size="md" disableProfileClick />
          </button>

          {isUserMenuOpen && (
            <>
              {/* Invisible Backdrop to close dropdown on outside click */}
              <div
                className="fixed inset-0 z-40"
                onClick={() => setIsUserMenuOpen(false)}
              />

              <div className="absolute right-0 mt-2 w-[calc(100vw-2rem)] sm:w-72 max-w-xs bg-white dark:bg-[#181b20] rounded-3xl shadow-2xl border border-[#d5d8de] dark:border-surface-800 p-2 z-50 animate-scale-up font-sans">
                {/* User Summary Card */}
                <div className="p-3 bg-surface-50 dark:bg-surface-850 rounded-2xl border border-surface-200/60 dark:border-surface-700/50 mb-1.5">
                  <div className="flex items-center space-x-3 mb-2.5">
                    <div className="relative">
                      <Avatar
                        src={user?.avatarUrl}
                        name={user?.fullName || 'User'}
                        size="md"
                        disableProfileClick
                      />
                      <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-500 ring-2 ring-white dark:ring-surface-900" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="font-extrabold text-sm text-[#121417] dark:text-white truncate">
                        {user?.fullName}
                      </div>
                      <div className="text-[11px] text-[#667085] dark:text-surface-400 font-mono truncate">
                        {user?.email}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t border-surface-200/50 dark:border-surface-750 text-[10px]">
                    <RoleBadge role={user?.role} />
                    <span className="text-surface-500 font-medium truncate max-w-[140px]">
                      {user?.title || 'Team Contributor'}
                    </span>
                  </div>
                </div>

                {/* Status Indicator */}
                <div className="px-3 py-1.5 mb-1.5 flex items-center justify-between text-[11px] text-surface-600 dark:text-surface-300 rounded-xl bg-surface-50/50 dark:bg-surface-800/40">
                  <span className="flex items-center space-x-1.5">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                    <span className="font-semibold text-emerald-600 dark:text-emerald-400">Online & Available</span>
                  </span>
                  <span className="text-[10px] text-surface-400">Pulse</span>
                </div>

                <div className="space-y-0.5">
                  {/* Edit Profile & Photo */}
                  <button
                    onClick={() => {
                      setIsUserMenuOpen(false);
                      setIsEditProfileOpen(true);
                    }}
                    className="w-full text-left px-3 py-2 text-xs font-bold text-[#121417] dark:text-white hover:bg-surface-100 dark:hover:bg-surface-800 rounded-2xl flex items-center justify-between transition-colors group"
                  >
                    <div className="flex items-center space-x-2.5">
                      <UserIcon className="w-4 h-4 text-brand-500 group-hover:scale-110 transition-transform" />
                      <span>Edit Profile & Photo</span>
                    </div>
                    <span className="text-[10px] text-surface-400 font-normal">Avatar</span>
                  </button>

                  {/* Change Password */}
                  <button
                    onClick={() => {
                      setIsUserMenuOpen(false);
                      setIsChangePasswordOpen(true);
                    }}
                    className="w-full text-left px-3 py-2 text-xs font-bold text-[#121417] dark:text-white hover:bg-surface-100 dark:hover:bg-surface-800 rounded-2xl flex items-center space-x-2.5 transition-colors group"
                  >
                    <Key className="w-4 h-4 text-amber-500 group-hover:scale-110 transition-transform" />
                    <span>Change Password</span>
                  </button>

                  {/* Desktop notifications: asked once on first use, changeable here any time */}
                  {notificationsSupported && (
                    <button
                      onClick={() => {
                        if (browserPermission === 'default') requestNotificationPermission();
                      }}
                      disabled={browserPermission !== 'default'}
                      className="w-full text-left px-3 py-2 text-xs font-bold text-[#121417] dark:text-white hover:bg-surface-100 dark:hover:bg-surface-800 disabled:hover:bg-transparent dark:disabled:hover:bg-transparent rounded-2xl flex items-center justify-between transition-colors group disabled:cursor-default"
                      title={
                        browserPermission === 'denied'
                          ? 'Blocked in the browser. Click the lock icon next to the address bar, then allow Notifications for this site.'
                          : undefined
                      }
                    >
                      <div className="flex items-center space-x-2.5">
                        <Bell className="w-4 h-4 text-orange-500 group-hover:scale-110 transition-transform" />
                        <span>Desktop notifications</span>
                      </div>
                      <span
                        className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                          browserPermission === 'granted'
                            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                            : browserPermission === 'denied'
                            ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300'
                            : 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300'
                        }`}
                      >
                        {browserPermission === 'granted' ? 'On' : browserPermission === 'denied' ? 'Blocked' : 'Turn on'}
                      </span>
                    </button>
                  )}

                  {/* Theme Toggle in Menu */}
                  <button
                    onClick={toggleTheme}
                    className="w-full text-left px-3 py-2 text-xs font-bold text-[#121417] dark:text-white hover:bg-surface-100 dark:hover:bg-surface-800 rounded-2xl flex items-center justify-between transition-colors group"
                  >
                    <div className="flex items-center space-x-2.5">
                      {theme === 'dark' ? (
                        <Sun className="w-4 h-4 text-amber-400 group-hover:scale-110 transition-transform" />
                      ) : (
                        <Moon className="w-4 h-4 text-indigo-500 group-hover:scale-110 transition-transform" />
                      )}
                      <span>Appearance</span>
                    </div>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-surface-200 dark:bg-surface-700 text-surface-700 dark:text-surface-300 font-semibold">
                      {theme === 'dark' ? 'Dark' : 'Light'}
                    </span>
                  </button>

                  {/* Product Tour */}
                  <button
                    onClick={() => {
                      setIsUserMenuOpen(false);
                      startTour();
                    }}
                    className="w-full text-left px-3 py-2 text-xs font-bold text-[#121417] dark:text-white hover:bg-surface-100 dark:hover:bg-surface-800 rounded-2xl flex items-center space-x-2.5 transition-colors group"
                  >
                    <Sparkles className="w-4 h-4 text-brand-500 group-hover:scale-110 transition-transform" />
                    <span>Product Tour</span>
                  </button>
                </div>

                <div className="my-1.5 border-t border-[#eaecf0] dark:border-surface-800" />

                {/* Sign Out */}
                <button
                  onClick={() => {
                    setIsUserMenuOpen(false);
                    logout();
                  }}
                  className="w-full text-left px-3 py-2.5 text-xs font-bold text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded-2xl flex items-center space-x-2.5 transition-colors"
                >
                  <LogOut className="w-4 h-4 text-rose-500" />
                  <span>Sign Out</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Edit Profile & Photo Modal */}
      <EditProfileModal
        isOpen={isEditProfileOpen}
        onClose={() => setIsEditProfileOpen(false)}
      />

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={isChangePasswordOpen}
        onClose={() => setIsChangePasswordOpen(false)}
      />

      {/* ClickUp-Style Workspace Invite Modal */}
      <InviteModal
        isOpen={isInviteOpen}
        onClose={() => setIsInviteOpen(false)}
      />
    </header>
  );
};
